import React, { useEffect, useState } from 'react';
import ContractReportTemplate from '../../_components/templates/ContractReportTemplate';
import TabSelection from '../../_components/molecules/TabSelection';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  CardComponent,
  ClauseAnalysisAccordion,
  ComplianceAnalysis,
  ComplianceCheckInfo,
  ComplianceDonutCard,
  GeneralCardComponent,
  NoClauseVisual,
  RegulationsCard,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { useLocation, useNavigate } from 'react-router-dom';
import contractAIQConstants from '../../core-utils/messages/ContractAIQConstants';
import properties from '../../core-utils/properties';
import axios from 'axios';
import {
  getReportBreadcrumbList,
  TitleAndSubTitle,
} from '../../core-utils/helper-utils/contractAIQHelper';
import Loader from '../../_components/molecules/Loader';
import { scrollBarStyles } from '../../core-utils/messages/CommonConstants';
import { useStores } from '../../stores';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import Snackbar from '../../_components/atoms/Snackbar';
import { handleDownloadReport } from '../../core-utils/helper-utils/contractReportHelper';

//styles
const useStyles = makeStyles()({
  tabContent: {
    paddingTop: '24px',
  },
  cards: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '24px',
    marginBottom: '29px',
  },
  flaggedAnalysisCards: {
    display: 'flex',
    gap: '16px',
    margin: '32px 0px',
  },
  flaggedAnalysisCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    height: '234px',
    overflow: 'auto',
    ...scrollBarStyles,
  },
  sections: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
  },
  complianceChecks: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
});

const CompensationTransparencyReport = () => {
  //constants
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    reportName,
    document,
    contractId,
    draftId,
    isDraft,
  }: any = location?.state;
  const breadcrumbs = getReportBreadcrumbList(
    navigate,
    location?.state,
    isDraft,
  );
  const constants =
    contractAIQConstants.CONTRACT_REPORTS.COMPENSATION_TRANSPARENCY_REPORT;
  const questionAndAnswers: { question: string; definition: string }[] =
    constants.QUESTIONS;
  const employerGroupStore = useStores().employerGroupStore;
  const employerName =
    employerGroupStore?.employerGroupResponseData?.employer_group_name[0];
  const clauseAlignmentSubtitle = constants.CLAUSE_ALIGNMENT_ANALYSIS.SUBTITLE;
  const clauseAlignmentDefinition = `${clauseAlignmentSubtitle.PART1}${employerName} ASA ${clauseAlignmentSubtitle.PART2}`;
  const divRef = React.useRef<any>(null);

  //states
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<{ id: string; index?: number }>({
    id: 'summary',
    index: 0,
  });
  const [redFlagAnalysis, setRedFlagAnalysis] = useState<any[]>([]);
  const [clauseAlignmentAnalysis, setClauseAlignmentAnalysis] = useState<any>({
    extract_clauses: '',
    analysis: '',
    clause: '',
  });
  const [clickedSection, setClickedSection] = useState<string>('');
  const [redFlagsCount, setRedFlagsCount] = useState<number>(0);
  const [clearCount, setClearCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loader] = useState<any>(<Loader />);
  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });

  //APIs
  const getCompensationTransparency = () => {
    const url =
      properties.aiqBaseUrl +
      properties.contractBaseUrl +
      properties.compensationTransparencyReport;
    return axios.post(url, {
      ...(isDraft ? { draft_id: draftId } : { contract_id: contractId }),
    });
  };

  const getAllApis = async () => {
    await Promise.allSettled([getCompensationTransparency()]).then(
      (responses: any) => {
        if (responses[0]?.status === 'fulfilled') {
          const compensationData = responses[0]?.value?.data?.data;
          const redFlagAnalysisData = compensationData?.redFlagAnalysis;
          setRedFlagAnalysis(redFlagAnalysisData);
          setClauseAlignmentAnalysis(compensationData?.clauseAlignmentAnalysis);
          let flagged = 0;
          let clear = 0;
          redFlagAnalysisData?.map((item: any) => {
            flagged += item?.notMetList?.length;
            clear += item?.metList?.length;
          });
          setRedFlagsCount(flagged);
          setClearCount(clear);
          setLoading(false);
        }
      },
    );
  };

  //functions
  const handleReportSummary = () => {
    setActiveTab({ id: 'summary', index: 0 });
    setSelectedTabIndex(0);
  };

  const handleCloseSnackBar = () => {
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };

  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };

  //function to get content of report summary tabs based on tab index
  const getTabContent = () => {
    switch (selectedTabIndex) {
      case 0:
        return <OverviewTab />;
      case 1:
        return <AboutTab />;
      default:
        return <></>;
    }
  };

  //function to get content on the right based on left tab selection
  const getActiveTabContent = () => {
    switch (activeTab.id) {
      case 'summary':
        return <ReportSummaryContent />;
      case 'red-flag-analysis':
        return <RedFlagAnalysis />;
      case 'clause-alignment-analysis':
        return <ClauseAlignmentAnalysis />;
      default:
        return <></>;
    }
  };

  //useEffects
  useEffect(() => {
    getAllApis();
  }, []);

  useEffect(() => {
    if (divRef.current) {
      divRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [clickedSection, divRef]);

  //components
  //component for list of Analysis on the left
  const Analysis = () => {
    return (
      <Grid>
        <RegulationsCard
          type={'Not Met'}
          title={constants.CLAUSE_ALIGNMENT_ANALYSIS.TITLE}
          description={``}
          count={``}
          tooltip={clauseAlignmentDefinition}
          handleOnclick={() => {
            setActiveTab({
              id: 'clause-alignment-analysis',
              index: 2,
            });
          }}
          isActive={activeTab.index === 2}
        />
        <RegulationsCard
          type={redFlagsCount > 0 ? 'Not Met' : 'Met'}
          title={constants.RED_FLAG_ANALYSIS.TITLE}
          description={`${redFlagsCount} Flag${
            redFlagsCount === 1 ? '' : 's'
          } Identified`}
          count={`${redFlagsCount}`}
          tooltip={constants.RED_FLAG_ANALYSIS.SUBTITLE}
          handleOnclick={() => {
            setActiveTab({
              id: 'red-flag-analysis',
              index: 1,
            });
          }}
          isActive={activeTab.index === 1}
        />
      </Grid>
    );
  };

  //component for report summary content
  const ReportSummaryContent = () => {
    return (
      <>
        <TabSelection
          selectedTabIndex={selectedTabIndex}
          setSelectedTabIndex={setSelectedTabIndex}
          tabList={constants.SUMMARY_TABS}
        />
        <Grid className={classes.tabContent}>{getTabContent()}</Grid>
      </>
    );
  };

  //component for red flag analysis
  const RedFlagAnalysis = () => {
    return (
      <Grid>
        {TitleAndSubTitle(
          constants.RED_FLAG_ANALYSIS.TITLE,
          constants.RED_FLAG_ANALYSIS.SUBTITLE,
        )}
        <Grid className={classes.flaggedAnalysisCards}>
          <ComplianceDonutCard
            data={[
              {
                label: 'Present',
                value: redFlagsCount,
                color: '#d6545e',
              },
              {
                label: 'Absent',
                value: clearCount,
                color: '#17a39c',
              },
            ]}
            title={'Total Red Flag'}
            subTitle={`${redFlagsCount}`}
          />
          <CardComponent
            title={
              <Typography
                fontSize="16px"
                fontType={'semiBold'}
                lineHeight="22.4px"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
                margin="0px 0px 16px 0px"
              >
                Red Flag Analyzed
              </Typography>
            }
            body={
              <Grid className={classes.flaggedAnalysisCard}>
                {redFlagAnalysis?.map((section: any) => {
                  const flaggedCount = section?.notMetList?.length;
                  return (
                    <ComplianceCheckInfo
                      infoText={section?.name}
                      bracketsText={`${
                        flaggedCount > 0
                          ? flaggedCount +
                            ` Red Flag${
                              flaggedCount === 1 ? '' : 's'
                            } Identified`
                          : 'No Red Flag'
                      }`}
                      type={flaggedCount > 0 ? 'Red' : 'Green'}
                      handleOnclick={() => {
                        setClickedSection(section?.name);
                      }}
                    />
                  );
                })}
              </Grid>
            }
          />
        </Grid>
        <Grid className={classes.sections}>
          {redFlagAnalysis?.map((section: any, index: number) => {
            const flaggedCount = section?.notMetList?.length;
            const clearCount = section?.metList?.length;

            return (
              <Grid ref={clickedSection === section?.name ? divRef : null}>
                <ComplianceAnalysis
                  title={`Section ${index + 1}: ${section?.name}`}
                  defintion={section?.definition}
                  showZeroValueLegends={true}
                  content={
                    <>
                      <Grid className={classes.complianceChecks}>
                        {section?.notMetList?.map((clause: any) => (
                          <Grid>
                            <ClauseAnalysisAccordion
                              type={'Red'}
                              title={clause?.question}
                              analysis={
                                clause?.reason?.length > 0
                                  ? clause?.reason
                                  : undefined
                              }
                              extract={
                                clause?.exact_text?.length > 0
                                  ? clause?.exact_text
                                  : undefined
                              }
                              chipLabel="Flagged"
                              chipWidth="72px"
                              titleWidth="640px"
                            />
                          </Grid>
                        ))}
                      </Grid>
                      <Grid
                        className={classes.complianceChecks}
                        style={{ marginTop: '12px' }}
                      >
                        {section?.metList?.map((clause: any) => (
                          <Grid>
                            <ClauseAnalysisAccordion
                              type={'Green'}
                              title={clause?.question}
                              analysis={
                                clause?.reason?.length > 0
                                  ? clause?.reason
                                  : undefined
                              }
                              extract={
                                clause?.exact_text?.length > 0
                                  ? clause?.exact_text
                                  : undefined
                              }
                              chipLabel="Clear"
                              chipWidth="72px"
                              titleWidth="640px"
                              content={
                                !(
                                  clause?.reason?.length > 0 ||
                                  clause?.exact_text?.length > 0
                                ) && (
                                  <NoClauseVisual
                                    mainLabel={'Clear Clause'}
                                    subLabel={
                                      'There is no indication of non-compliance or conflict.'
                                    }
                                  />
                                )
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  }
                  legendsData={[
                    {
                      label: 'Flagged',
                      value: flaggedCount,
                      color: '#d6545e',
                    },
                    {
                      label: 'Clear',
                      value: clearCount,
                      color: '#17a39c',
                    },
                  ]}
                />
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    );
  };

  //Repetitive card component
  const ClauseCard = (title: string, background: string, content: string) => {
    return (
      <CardComponent
        title={
          <Typography
            fontSize="14px"
            lineHeight="24px"
            fontType="medium"
            textColor={patientAccessTheme.palette.text.highemphasis}
          >
            {title}
          </Typography>
        }
        mainGridStyles={{
          background: background,
          marginTop: '32px',
        }}
        body={getContentTypography(content)}
      />
    );
  };

  //component for clause alignment analysis
  const ClauseAlignmentAnalysis = () => {
    return (
      <Grid>
        {TitleAndSubTitle(
          constants.CLAUSE_ALIGNMENT_ANALYSIS.TITLE,
          clauseAlignmentDefinition,
        )}
        {ClauseCard(
          'Extracted Clauses',
          patientAccessTheme.palette.status.white!,
          clauseAlignmentAnalysis?.extract_clauses,
        )}
        {ClauseCard('Analysis', '#EDC0BB1A', clauseAlignmentAnalysis?.analysis)}
        {ClauseCard('Clause', '#9DDDEB1A', clauseAlignmentAnalysis?.clause)}
      </Grid>
    );
  };

  //components for repetitive Typography
  const getContentTypography = (label: string, italic?: boolean) => {
    return (
      <Typography
        fontSize="14px"
        lineHeight="24px"
        fontType="regular"
        textColor={patientAccessTheme.palette.text.highemphasis}
        styleProps={{
          fontStyle: italic ? 'italic' : 'normal',
          whiteSpace: 'pre-line',
        }}
        display="inline"
      >
        {label}
      </Typography>
    );
  };

  //component for overview tab content
  const OverviewTab = () => {
    return (
      <Grid>
        <GeneralCardComponent
          title={'Objective of Report'}
          body={
            <>
              {getContentTypography(
                `The purpose of this report is to conduct a two-fold analysis of the ${employerName} Administrative Services Agreement (ASA) in line with regulatory standards regarding compensation transparency under the Consolidated Appropriations Act (CAA) of 2021 and ERISA Section 408(b)(2). The report consists of:`,
              )}
              <ol>
                <li style={{ marginLeft: '18px', fontSize: '14px' }}>
                  {getContentTypography(
                    `Red Flag Analysis: Identifying potential red flags related to compensation terms, conflicts of interest, and fiduciary obligations that could indicate non-compliance with ERISA fiduciary requirements and the CAA’s transparency standards.`,
                  )}
                </li>
                <li style={{ marginLeft: '18px', fontSize: '14px' }}>
                  {getContentTypography(
                    `Clause Alignment Analysis: Evaluating how the contract addresses the requirements for full compensation disclosure, including compensation from third parties, affiliates, and subcontractors, in line with ERISA Section 408(b)(2) and related regulatory standards.`,
                  )}
                </li>
              </ol>
            </>
          }
          headerStyles={{ padding: '16px' }}
          bodyStyles={{ padding: '16px' }}
          mainGridStyles={{ marginBottom: '24px' }}
          width="100%"
        />
        <GeneralCardComponent
          title={'Summary'}
          body={
            <>
              {getContentTypography(
                `Contract analysis identified ${
                  redFlagsCount > 0
                    ? redFlagsCount + ' Red Flags, particularly around '
                    : 'No Red Flags.'
                }`,
              )}
              {redFlagAnalysis?.length !== 0 &&
                redFlagAnalysis?.map((section: any, index: number) => {
                  const flaggedCount = section?.notMetList?.length;
                  return (
                    <>
                      {getContentTypography(
                        `${section?.name}(${
                          flaggedCount > 0
                            ? flaggedCount +
                              ` Red Flag${flaggedCount === 1 ? '' : 's'}`
                            : 'No Red Flag'
                        })${
                          index !== redFlagAnalysis?.length - 1 ? ', ' : '.'
                        }`,
                      )}
                    </>
                  );
                })}
            </>
          }
          headerStyles={{ padding: '16px' }}
          bodyStyles={{ padding: '16px' }}
          width="100%"
        />
      </Grid>
    );
  };

  const AboutTab = () => {
    return (
      <Grid>
        <Grid className={classes.cards}>
          {questionAndAnswers?.map((item) => {
            return (
              <GeneralCardComponent
                title={item?.question}
                body={getContentTypography(item?.definition)}
                headerStyles={{ padding: '16px' }}
                bodyStyles={{ padding: '16px' }}
                width="calc(50% - 12px)"
              />
            );
          })}
        </Grid>
        <GeneralCardComponent
          title={'Sample Clause'}
          body={getContentTypography(
            `Compensation Fee Disclosure. Claims Administrator shall, at such time as required by applicable regulations promulgated at 29 C.F.R. 2550.408b-2 (the “Fee Disclosure Regulation”), disclose to Group in writing all “compensation” to be received by Claims Administrator and its “affiliates” and “subcontractors” (each such term as defined in the Fee Disclosure Regulation) in connection with the Services provided hereunder and shall disclose any changes in such information in accordance with the requirements of the Fee Disclosure Regulation.`,
            true,
          )}
          headerStyles={{
            padding: '16px 16px 10px',
            background: patientAccessTheme.palette.grey[400],
            height: 'auto',
          }}
          mainGridStyles={{
            borderRadius: '0px',
          }}
          bodyStyles={{
            padding: '0px 16px 16px 16px',
            background: patientAccessTheme.palette.grey[400],
          }}
          width="100%"
        />
      </Grid>
    );
  };

  return (
    <>
      {loading ? (
        loader
      ) : (
        <ContractReportTemplate
          breadcrumbsList={breadcrumbs}
          reportName={`${reportName} of`}
          document={document}
          handleDocumentOnclick={() => {}}
          handleDownloadReport={() =>
            handleDownloadReport(
              'compensationTransparencyReport',
              employerGroupStore?.employerGroupResponseData
                ?.employer_group_name[0],
              isDraft ? '' : contractId,
              handleOpenSnackBar,
              '',
              '',
              '',
              isDraft ? draftId : '',
            )
          }
          handleReportSummary={handleReportSummary}
          isReportSummaryActive={activeTab.id === 'summary'}
          leftContent={<Analysis />}
          rightContent={getActiveTabContent()}
          showDownloadReport={true}
        />
      )}
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </>
  );
};

export default CompensationTransparencyReport;
